// Hook: GET /api/firm/counts -> FirmCounts, or undefined while loading or
// on error. Deliberately NO fixture fallback: a rail badge that cannot
// know the number shows nothing rather than an invented one.

import { useQuery } from "@tanstack/react-query";
import type { FirmCountsResponse } from "@duralex/shared";
import { fetchFirmCounts, SERVER_TO_FRONTEND_PA } from "../api/mock";
import type { FirmCounts } from "../data/matters";
import { PRACTICE_AREA_ORDER } from "../data/practice-areas";

function mapPracticeAreas(serverByArea: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const id of PRACTICE_AREA_ORDER) out[id] = 0;
  out.other = 0;
  for (const [serverKey, count] of Object.entries(serverByArea)) {
    const feId = SERVER_TO_FRONTEND_PA[serverKey];
    if (feId) out[feId] = (out[feId] ?? 0) + count;
  }
  return out;
}

// Absent from an older API it stays absent: the card then keeps loading
// rather than showing an area with no stages.
function mapPipeline(server: FirmCountsResponse["pipelineByArea"]): FirmCounts["pipelineByArea"] {
  if (!server) return undefined;
  const out: NonNullable<FirmCounts["pipelineByArea"]> = {};
  for (const [serverKey, stages] of Object.entries(server)) {
    const feId = SERVER_TO_FRONTEND_PA[serverKey];
    if (feId) out[feId] = stages;
  }
  return out;
}

export function useFirmCounts(): FirmCounts | undefined {
  const { data } = useQuery({
    queryKey: ["firm", "counts"],
    queryFn: fetchFirmCounts,
    staleTime: 60_000,
  });

  if (!data) return undefined;

  return {
    activeMatters: data.counts.activeMatters,
    needsAttention: data.counts.needsAttention,
    readyToFile: data.counts.readyToFile,
    deadlinesCritical: data.counts.deadlinesCritical,
    deadlinesThisWeek: data.counts.deadlinesThisWeek,
    deadlinesOverdue: data.counts.deadlinesOverdue ?? 0,
    clients: data.counts.clients,
    unmatched: data.counts.unmatched,
    // A payload from an older API carries no key; 0 hides the rail item.
    needsReview: data.counts.needsReview ?? 0,
    byPracticeArea: mapPracticeAreas(data.byPracticeArea),
    pipelineByArea: mapPipeline(data.pipelineByArea),
  };
}
